<template>
  <Teleport to="body">
    <Transition name="bottom-sheet-backdrop">
      <div v-if="isOpen"
        class="fixed w-screen h-screen bg-black/10 backdrop-blur-xs inset-0 z-40"
        :class="{ 'md:bg-transparent md:backdrop-blur-none': variant === 'sidebar' }"
        @click="closeSheet"></div>
    </Transition>

    <Transition :name="transitionName">
        <div v-if="isOpen"
          data-testid="bottom-sheet"
          class="bottom-sheet-panel fixed z-50 left-0 right-0 bottom-0 h-11/12 bg-background shadow-2xl overflow-hidden pb-safe"
          :class="variant === 'sidebar'
            ? 'md:left-auto md:top-0 md:bottom-0 md:right-0 md:h-full md:max-w-md md:w-full md:rounded-none md:pb-0 md:border-l md:border-border'
            : 'md:inset-0 md:m-auto md:max-w-md md:h-fit md:max-h-[85vh] md:rounded-2xl md:pb-0 md:border md:border-border'"
          @click.stop>
          <div class="h-full flex flex-col" :class="{ 'md:max-h-[85vh]': variant === 'modal' }">
            <!-- Header: back arrow on mobile, close X on desktop -->
            <div v-if="showHeader" class="flex items-center justify-left p-4 flex-shrink-0 cursor-pointer md:hidden" @click="closeSheet">
              <ArrowLeftIcon
                class="h-6 w-6 text-foreground hover:text-muted-foreground transition-colors" />
              <span
                class="text-foreground hover:text-muted-foreground font-semibold transition-colors ml-2">
                {{ $t('dialogs.close') }}
              </span>
            </div>
            <button v-if="showHeader"
              class="hidden md:flex absolute top-3 right-3 z-10 h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              :aria-label="$t('dialogs.close')"
              @click="closeSheet">
              <XMarkIcon class="h-5 w-5" />
            </button>

            <!-- Contenu scrollable -->
            <div class="flex-1 overflow-y-auto px-6" :class="{ 'pt-4': !showHeader, 'pb-4': !showFooter, 'md:pt-6': showHeader }">
              <slot />
            </div>

            <!-- Footer fixe avec bouton -->
            <div v-if="showFooter" class="px-6 py-4 border-t border-border bg-background flex-shrink-0">
              <button @click="closeSheet"
                class="w-full py-3 text-center font-medium text-muted-foreground hover:text-foreground transition-colors">
                {{ $t('dialogs.close') }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { watch, nextTick, computed } from 'vue'

interface Props {
  isOpen: boolean
  showHeader?: boolean
  showFooter?: boolean
  variant?: 'modal' | 'sidebar'
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  showHeader: true,
  showFooter: false,
  variant: 'modal'
})
const emit = defineEmits<Emits>()

const transitionName = computed(() =>
  props.variant === 'sidebar' ? 'bottom-sheet-sidebar' : 'bottom-sheet'
)

const closeSheet = () => {
  emit('update:isOpen', false)
}

// Prevent body scroll when sheet is open
watch(() => props.isOpen, (isOpen) => {
  nextTick(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  })
})
</script>

<style scoped>
.bottom-sheet-backdrop-enter-active,
.bottom-sheet-backdrop-leave-active {
  transition: opacity 300ms ease-out;
}
.bottom-sheet-backdrop-enter-from,
.bottom-sheet-backdrop-leave-to {
  opacity: 0;
}

/* Modal variant (default): slide up on mobile, fade + scale centered on desktop */
.bottom-sheet-enter-active,
.bottom-sheet-leave-active {
  transition: translate 300ms ease-out, opacity 300ms ease-out, scale 300ms ease-out;
}

.bottom-sheet-enter-from,
.bottom-sheet-leave-to {
  translate: 0 100%;
  opacity: 0;
}

@media (min-width: 768px) {
  .bottom-sheet-enter-from,
  .bottom-sheet-leave-to {
    translate: 0 0;
    scale: 0.95;
    opacity: 0;
  }
}

/* Sidebar variant: slide up on mobile, slide from the right on desktop */
.bottom-sheet-sidebar-enter-active,
.bottom-sheet-sidebar-leave-active {
  transition: translate 300ms ease-out, opacity 300ms ease-out;
}

.bottom-sheet-sidebar-enter-from,
.bottom-sheet-sidebar-leave-to {
  translate: 0 100%;
  opacity: 0;
}

@media (min-width: 768px) {
  .bottom-sheet-sidebar-enter-from,
  .bottom-sheet-sidebar-leave-to {
    translate: 100% 0;
    opacity: 1;
  }
}
</style>
